import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

// ─── Types ────────────────────────────────────────────────────────────────────
export type TileType =
  | 'applications-today'
  | 'applications-yesterday'
  | 'registered-today'
  | 'registered-yesterday'
  | 'this-week'
  | 'this-month';

interface TileDetailModalProps {
  type: TileType;
  onClose: () => void;
  applications?: any[]; // pass in the applications array for app-based tiles
}

interface UserRow {
  _id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  createdAt: string;
  applicationCount: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const TILE_CONFIG: Record<TileType, { title: string; color: string; icon: string; accent: string }> = {
  'applications-today': { title: 'Applications Today', color: '#0284c7', icon: '📄', accent: '#e0f2fe' },
  'applications-yesterday': { title: 'Applications Yesterday', color: '#f97316', icon: '📄', accent: '#fff7ed' },
  'registered-today':   { title: 'Registered Today',   color: '#3b82f6', icon: '👤', accent: '#dbeafe' },
  'registered-yesterday': { title: 'Registered Yesterday', color: '#ec4899', icon: '🕐', accent: '#fce7f3' },
  'this-week':          { title: 'This Week',           color: '#10b981', icon: '📅', accent: '#d1fae5' },
  'this-month':         { title: 'This Month',          color: '#8b5cf6', icon: '📊', accent: '#ede9fe' },
};

const PAGE_SIZES = [10, 20, 30, 50, 'All'] as const;

function formatDate(d: string) {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDateRange(type: TileType): { startDate: string; endDate: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayStr = toLocalDateStr(today);

  if (type === 'registered-today') {
    return { startDate: todayStr, endDate: todayStr };
  }
  if (type === 'registered-yesterday') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    const yStr = toLocalDateStr(y);
    return { startDate: yStr, endDate: yStr };
  }
  return { startDate: '', endDate: '' };
}

function filterApplications(apps: any[], type: TileType): any[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (type === 'applications-today') {
    return apps.filter(a => {
      const c = new Date(a.createdAt);
      return c >= today && a.applicationId?.startsWith('CNG-');
    });
  }
  if (type === 'applications-yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return apps.filter(a => {
      const c = new Date(a.createdAt);
      return c >= yesterday && c < today && a.applicationId?.startsWith('CNG-');
    });
  }
  if (type === 'this-week') {
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    return apps.filter(a => new Date(a.createdAt) >= weekAgo);
  }
  if (type === 'this-month') {
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    return apps.filter(a => new Date(a.createdAt) >= monthAgo);
  }
  return [];
}

function computeCompletion(app: any): number {
  try {
    if (!app) return 0;
    if (app.status?.toLowerCase() === 'payment_complete') return 100;
    if (app.isComplete || app.isSubmitted) return 100;
    const fd = app.formData || {};
    const entries = Object.entries(fd).filter(([k]) => k !== 'errors');
    if (entries.length === 0) return 0;
    let filled = 0, total = 0;
    for (const [, v] of entries) {
      total++;
      if (v === null || v === undefined) continue;
      if (typeof v === 'string') { if (v.trim() !== '') filled++; }
      else if (typeof v === 'boolean') { if (v) filled++; }
      else if (typeof v === 'number') filled++;
      else if (Array.isArray(v)) { if (v.length > 0) filled++; }
      else if (typeof v === 'object') { if (Object.keys(v).length > 0) filled++; }
    }
    return Math.max(0, Math.min(100, Math.round((filled / Math.max(1, total)) * 100)));
  } catch { return 0; }
}

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideDown = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(30px) scale(0.97); }
`;

const waterfall = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Styled components ────────────────────────────────────────────────────────
const Overlay = styled.div<{ $closing: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 99998;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(6px);
  animation: ${p => p.$closing ? fadeOut : fadeIn} 0.3s ease forwards;
`;

const ModalContainer = styled.div<{ $closing: boolean; $accent: string }>`
  background: #ffffff;
  border-radius: 18px;
  width: 92vw;
  max-width: 1050px;
  max-height: 82vh;
  overflow: hidden;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  animation: ${p => p.$closing ? slideDown : slideUp} 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`;

const Header = styled.div<{ $color: string; $accent: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 28px;
  background: linear-gradient(135deg, ${p => p.$accent} 0%, #ffffff 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const IconBadge = styled.div<{ $color: string }>`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: ${p => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 4px 12px ${p => p.$color}44;
`;

const HeaderTitle = styled.div`
  h3 { margin: 0; font-size: 1.1rem; font-weight: 700; color: #1e293b; }
  span { font-size: 0.78rem; color: #64748b; font-weight: 500; }
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  color: #64748b;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  &:hover { background: rgba(239, 68, 68, 0.12); color: #ef4444; transform: scale(1.08); }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
`;

const Th = styled.th`
  padding: 12px 16px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
`;

const Tr = styled.tr<{ $index: number; $key: string }>`
  opacity: 0;
  animation: ${waterfall} 0.4s ease forwards;
  animation-delay: ${p => Math.min(p.$index * 40, 600)}ms;
  transition: background 0.15s;
  &:hover { background: #f1f5f9; }
`;

const Td = styled.td`
  padding: 11px 16px;
  font-size: 0.82rem;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
  ${p => {
    const s = (p.$status || '').toLowerCase();
    if (s === 'payment_complete' || s === 'paid') return css`background: #dcfce7; color: #15803d;`;
    if (s === 'approved') return css`background: #d1fae5; color: #047857;`;
    if (s === 'draft') return css`background: #fef3c7; color: #b45309;`;
    if (s === 'in-review') return css`background: #e0e7ff; color: #4338ca;`;
    if (s === 'rejected') return css`background: #fee2e2; color: #b91c1c;`;
    return css`background: #f1f5f9; color: #475569;`;
  }}
`;

const CompletionBar = styled.div<{ $pct: number }>`
  display: flex;
  align-items: center;
  gap: 8px;
  .bar { width: 60px; height: 6px; background: #e2e8f0; border-radius: 3px; overflow: hidden; }
  .fill { height: 100%; border-radius: 3px; background: ${p => p.$pct >= 80 ? '#22c55e' : p.$pct >= 40 ? '#f59e0b' : '#ef4444'}; width: ${p => p.$pct}%; transition: width 0.6s ease; }
  span { font-size: 0.72rem; color: #64748b; font-weight: 600; min-width: 30px; }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-top: 1px solid #e2e8f0;
  background: #fafbfc;
  flex-wrap: wrap;
  gap: 10px;
`;

const PageSizeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PageSizeBtn = styled.button<{ $active: boolean }>`
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 0.73rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${p => p.$active ? '#4A90E2' : '#e2e8f0'};
  background: ${p => p.$active ? '#eef5ff' : '#fff'};
  color: ${p => p.$active ? '#2563eb' : '#64748b'};
  &:hover { border-color: #4A90E2; color: #2563eb; }
`;

const PaginationInfo = styled.span`
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 500;
`;

const PaginationBtns = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PageBtn = styled.button<{ $active?: boolean }>`
  min-width: 34px;
  height: 34px;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${p => p.$active ? '#4A90E2' : '#e2e8f0'};
  background: ${p => p.$active ? '#4A90E2' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#475569'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  &:hover:not(:disabled) { border-color: #4A90E2; background: ${p => p.$active ? '#3b82f6' : '#f0f7ff'}; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const EmptyState = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9rem;
  .icon { font-size: 2.5rem; margin-bottom: 12px; opacity: 0.5; }
`;

const LoadingSpinner = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: #94a3b8;
  .dot {
    display: inline-block;
    width: 10px; height: 10px;
    border-radius: 50%;
    background: #4A90E2;
    margin: 0 4px;
    animation: ${keyframes`0%,80%,100%{transform:scale(0)} 40%{transform:scale(1)}`} 1.2s infinite ease-in-out both;
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────
const TileDetailModal: React.FC<TileDetailModalProps> = ({ type, onClose, applications = [] }) => {
  const config = TILE_CONFIG[type];
  const isUserTile = type === 'registered-today' || type === 'registered-yesterday';

  const [closing, setClosing] = useState(false);
  const [pageSize, setPageSize] = useState<number | 'All'>(10);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Application tile data (client-side)
  const [appRows, setAppRows] = useState<any[]>([]);
  // Registered user tile data (server-side)
  const [userRows, setUserRows] = useState<UserRow[]>([]);
  const [userTotal, setUserTotal] = useState(0);

  // A unique key to force re-render waterfall on page change
  const [animKey, setAnimKey] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Graceful close
  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose(), 300);
  }, [onClose]);

  // Esc key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose]);

  // Load application data (client-side filter)
  useEffect(() => {
    if (!isUserTile) {
      const filtered = filterApplications(applications, type);
      filtered.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setAppRows(filtered);
    }
  }, [applications, type, isUserTile]);

  // Fetch registered users data (server-side)
  const fetchUsers = useCallback(async (pg: number, ps: number | 'All') => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange(type);
      const limit = ps === 'All' ? 9999 : ps;
      const params = new URLSearchParams({
        page: String(pg),
        limit: String(limit),
        sortBy: 'createdAt',
        sortOrder: 'desc',
        startDate,
        endDate,
      });
      const res = await fetch(`${API_BASE}/api/admin/registered-users?${params}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setUserRows(data.users);
        setUserTotal(data.total);
      }
    } catch (err) {
      console.error('[TileDetailModal] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    if (isUserTile) {
      fetchUsers(page, pageSize);
    }
  }, [isUserTile, page, pageSize, fetchUsers]);

  // Re-trigger waterfall on page/pageSize change
  useEffect(() => {
    setAnimKey(k => k + 1);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [page, pageSize]);

  // Pagination logic
  const totalItems = isUserTile ? userTotal : appRows.length;
  const effectivePageSize = pageSize === 'All' ? totalItems : pageSize;
  const totalPages = Math.max(1, Math.ceil(totalItems / Math.max(1, effectivePageSize)));
  const currentPage = Math.min(page, totalPages);

  // Slice rows for display
  const displayRows = isUserTile
    ? userRows
    : appRows.slice((currentPage - 1) * effectivePageSize, currentPage * effectivePageSize);

  const handlePageSizeChange = (ps: number | 'All') => {
    setPageSize(ps);
    setPage(1);
  };

  // Page numbers to render
  const getPageNumbers = () => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <Overlay $closing={closing} onClick={handleClose}>
      <ModalContainer $closing={closing} $accent={config.accent} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <Header $color={config.color} $accent={config.accent}>
          <HeaderLeft>
            <IconBadge $color={config.color}>{config.icon}</IconBadge>
            <HeaderTitle>
              <h3>{config.title}</h3>
              <span>{totalItems} {totalItems === 1 ? 'entry' : 'entries'} found</span>
            </HeaderTitle>
          </HeaderLeft>
          <CloseBtn onClick={handleClose} title="Close (Esc)">&times;</CloseBtn>
        </Header>

        {/* Body */}
        <Body ref={bodyRef}>
          {loading ? (
            <LoadingSpinner>
              <div><span className="dot" /><span className="dot" /><span className="dot" /></div>
              <div style={{ marginTop: 14, fontSize: '0.82rem' }}>Loading...</div>
            </LoadingSpinner>
          ) : displayRows.length === 0 ? (
            <EmptyState>
              <div className="icon">📭</div>
              No data available for this period.
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>#</Th>
                  {isUserTile ? (
                    <>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Phone</Th>
                      <Th>Registered At</Th>
                      <Th>Applications</Th>
                    </>
                  ) : (
                    <>
                      <Th>Application ID</Th>
                      <Th>Email</Th>
                      <Th>Status</Th>
                      <Th>Created At</Th>
                      <Th>Completion</Th>
                    </>
                  )}
                </tr>
              </Thead>
              <tbody>
                {displayRows.map((row: any, i: number) => {
                  const serial = (currentPage - 1) * effectivePageSize + i + 1;
                  return (
                    <Tr key={`${animKey}-${row._id || i}`} $index={i} $key={`${animKey}-${i}`}>
                      <Td style={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.75rem' }}>{serial}</Td>
                      {isUserTile ? (
                        <>
                          <Td style={{ fontWeight: 600, color: '#1e293b' }}>{row.fullName || '—'}</Td>
                          <Td>{row.email}</Td>
                          <Td>{row.phoneNumber || '—'}</Td>
                          <Td>{formatDate(row.createdAt)}</Td>
                          <Td>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: 26, height: 26, borderRadius: '50%', fontSize: '0.75rem', fontWeight: 700,
                              background: row.applicationCount > 0 ? '#dbeafe' : '#f1f5f9',
                              color: row.applicationCount > 0 ? '#2563eb' : '#94a3b8',
                            }}>
                              {row.applicationCount}
                            </span>
                          </Td>
                        </>
                      ) : (
                        <>
                          <Td style={{ fontWeight: 600, color: '#1e293b', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                            {row.applicationId || '—'}
                          </Td>
                          <Td>{row.email}</Td>
                          <Td><StatusBadge $status={row.status || 'draft'}>{(row.status || 'draft').replace(/_/g, ' ')}</StatusBadge></Td>
                          <Td>{formatDate(row.createdAt)}</Td>
                          <Td>
                            <CompletionBar $pct={computeCompletion(row)}>
                              <div className="bar"><div className="fill" /></div>
                              <span>{computeCompletion(row)}%</span>
                            </CompletionBar>
                          </Td>
                        </>
                      )}
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Body>

        {/* Footer / Pagination */}
        {totalItems > 0 && (
          <Footer>
            <PageSizeGroup>
              <span style={{ fontSize: '0.73rem', color: '#94a3b8', marginRight: 4 }}>Show:</span>
              {PAGE_SIZES.map(ps => (
                <PageSizeBtn
                  key={ps}
                  $active={pageSize === ps}
                  onClick={() => handlePageSizeChange(ps === 'All' ? 'All' : ps)}
                >
                  {ps}
                </PageSizeBtn>
              ))}
            </PageSizeGroup>

            <PaginationInfo>
              {pageSize === 'All'
                ? `Showing all ${totalItems} entries`
                : `${Math.min((currentPage - 1) * effectivePageSize + 1, totalItems)}–${Math.min(currentPage * effectivePageSize, totalItems)} of ${totalItems}`
              }
            </PaginationInfo>

            {pageSize !== 'All' && totalPages > 1 && (
              <PaginationBtns>
                <PageBtn disabled={currentPage === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                </PageBtn>
                {getPageNumbers().map((pg, idx) =>
                  pg === '...' ? (
                    <span key={`dots-${idx}`} style={{ padding: '0 4px', color: '#94a3b8' }}>…</span>
                  ) : (
                    <PageBtn key={pg} $active={currentPage === pg} onClick={() => setPage(pg as number)}>
                      {pg}
                    </PageBtn>
                  )
                )}
                <PageBtn disabled={currentPage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                </PageBtn>
              </PaginationBtns>
            )}
          </Footer>
        )}
      </ModalContainer>
    </Overlay>
  );
};

export default TileDetailModal;
